import React, {useState} from 'react';
import {Button, Card, Col, Form, Input, Row, Select, Slider, message, Result} from "antd";
import {CheckCircleFilled} from "@ant-design/icons";
import api from "../../api";

const SOLO_PRICE = 36;
const COMMISSION_RATE = 0.33;

const discountOptions = [
    {value: 0, label: '0% korting'},
    {value: 10, label: '10% korting'},
    {value: 25, label: '25% korting'},
    {value: 50, label: '50% korting'},
];

function formatIBAN(value) {
    if (!value) return value;
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    return cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
}

const benefits = [
    'Kies zelf de korting die je leden krijgen',
    'Ontvang 33% commissie op elke verkoop',
    'Binnen 24 uur goedgekeurd',
];

export default function AffiliateRegister() {
    const [form] = Form.useForm();
    const [discount, setDiscount] = useState(25);
    const [memberCount, setMemberCount] = useState(20);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const discountedPrice = (SOLO_PRICE * (1 - discount / 100)).toFixed(2);
    const commission = (discountedPrice * COMMISSION_RATE).toFixed(2);
    const yearlyEarnings = (commission * memberCount).toFixed(2);

    const onFinish = (values) => {
        if (values.iban) {
            values.iban = values.iban.replace(/\s/g, '').toUpperCase();
        }
        setLoading(true);
        api.submitAffiliateRequest(values)
            .then(() => {
                setSubmitted(true);
            })
            .catch(() => {
                message.error('Er is iets misgegaan. Probeer het opnieuw.');
            })
            .finally(() => setLoading(false));
    };

    if (submitted) {
        return (
            <Row justify="center" style={{minHeight: '80vh', alignItems: 'center', padding: '24px'}}>
                <Col xs={24} sm={20} md={16} lg={12}>
                    <Result
                        status="success"
                        title="Aanvraag ontvangen!"
                        subTitle="Bedankt voor je aanmelding. We nemen je aanvraag zo snel mogelijk in behandeling en nemen contact met je op via e-mail."
                    />
                </Col>
            </Row>
        );
    }

    return (
        <Row justify="center" style={{minHeight: '100vh', padding: '48px 24px'}}>
            <Col xs={24} sm={20} md={16} lg={10}>
                <Card>
                    <div style={{textAlign: 'center', marginBottom: 32}}>
                        <h1 style={{fontSize: 28, fontWeight: 700, marginBottom: 8}}>
                            Word Oogst.app partner
                        </h1>
                        <p style={{fontSize: 16, color: '#666', marginBottom: 24}}>
                            Bied je leden of klanten een korting aan en ontvang een jaarlijks terugkerende commissie zo lang gebruik maken van Oogst.app
                        </p>
                        <div style={{textAlign: 'left', maxWidth: 360, margin: '0 auto'}}>
                            {benefits.map((b, i) => (
                                <div key={i} style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 15}}>
                                    <CheckCircleFilled style={{color: '#52c41a', fontSize: 18, flexShrink: 0}} />
                                    <span>{b}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{member_discount: 25}}
                    >
                        <Form.Item
                            name="name"
                            label="Bedrijfsnaam / naam vereniging"
                            rules={[
                                {required: true, message: 'Naam is verplicht'},
                                {min: 3, message: 'Minimaal 3 karakters'}
                            ]}
                        >
                            <Input placeholder="bijv. Volkstuinvereniging De Wiebert" size="large"/>
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="E-mailadres"
                            rules={[
                                {required: true, message: 'E-mailadres is verplicht'},
                                {type: 'email', message: 'Vul een geldig e-mailadres in'}
                            ]}
                        >
                            <Input placeholder="bijv. info@volkstuinvereniging.nl" size="large"/>
                        </Form.Item>

                        <Form.Item
                            name="member_discount"
                            label="Korting voor je leden"
                            rules={[{required: true, message: 'Kies een korting'}]}
                        >
                            <Select
                                options={discountOptions}
                                onChange={setDiscount}
                                size="large"
                            />
                        </Form.Item>

                        {/* Earnings calculator */}
                        <div style={{
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 8,
                            padding: '16px 20px',
                            marginBottom: 24,
                        }}>
                            <div style={{fontWeight: 600, marginBottom: 8, fontSize: 15}}>
                                Met {discount}% korting:
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 4}}>
                                <span>Jouw leden betalen het eerste jaar:</span>
                                <strong>{'\u20AC'}{discountedPrice}</strong>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12}}>
                                <span>Jouw commissie per verkoop:</span>
                                <strong>{'\u20AC'}{commission}</strong>
                            </div>

                            <div style={{borderTop: '1px solid #b7eb8f', paddingTop: 12}}>
                                <div style={{marginBottom: 4, fontSize: 13, color: '#666'}}>
                                    Hoeveel mensen denk je te kunnen helpen met oogst.app? <strong>{memberCount}</strong>
                                </div>
                                <Slider
                                    min={5}
                                    max={200}
                                    value={memberCount}
                                    onChange={setMemberCount}
                                    trackStyle={{backgroundColor: '#52c41a'}}
                                    handleStyle={{borderColor: '#52c41a'}}
                                />
                                <div style={{
                                    textAlign: 'center',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: '#389e0d',
                                    marginTop: 4,
                                }}>
                                    Jouw jaarlijkse verdienste: {'\u20AC'}{yearlyEarnings}
                                </div>
                            </div>
                        </div>

                        <Form.Item
                            name="iban"
                            label="Rekeningnummer (IBAN)"
                            extra="Optioneel — kan ook later worden aangeleverd"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const cleaned = value.replace(/\s/g, '').toUpperCase();
                                        if (!/^NL\d{2}[A-Z]{4}\d{10}$/.test(cleaned)) {
                                            return Promise.reject('Vul een geldig Nederlands IBAN in (NL## BANK #### #### ##)');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            getValueFromEvent={(e) => formatIBAN(e.target.value)}
                        >
                            <Input placeholder="NL00 BANK 0000 0000 00" maxLength={22}/>
                        </Form.Item>

                        <Form.Item
                            name="kvk"
                            label="KVK-nummer (optioneel)"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        if (!/^\d{8}$/.test(value)) {
                                            return Promise.reject('KVK-nummer moet 8 cijfers zijn');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input placeholder="12345678" maxLength={8}/>
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                                Gratis aanmelden
                            </Button>
                            <div style={{textAlign: 'center', marginTop: 8, fontSize: 13, color: '#999'}}>
                                Geen verplichtingen — je kunt altijd stoppen
                            </div>
                        </Form.Item>
                    </Form>
                </Card>
            </Col>
        </Row>
    );
}
